<template>
  <Layout>
    <div class="mc-report__analysis-cost">
      <PageHeader :title="title" :subTitle="subTitle">
        <template #afterTitle>
          <div class="d-flex align-items-center gap-2">
            <McListDownloader
              :pageOptions="downloadPageOptions"
              :fileFormatOptions="['excel']"
              @download="handleDownload"
            />
            <McButtonBack href="/report" :query="{ domain: domain }" />
          </div>
        </template>
      </PageHeader>
      <McTable
        bordered
        withCard
        searchable
        :fields.sync="computedFields"
        :activeFilters="activeFilters"
        :apiClient="apiClient"
        :hasCheckbox="false"
        hasViewManager
      >
        <template #before-table>
          <div class="d-flex align-items-center justify-content-end gap-2">
            <FavoriteReportButton
              v-if="hasAccess(['favorite-reports:update']) && !report.isSaved"
              :report="report"
            />
            <McTableFilter
              :title="$t('report.title.reportParameter')"
              ref="mcTableFilter"
              :hasResetFilters="false"
              :activeFilters="activeFilters"
              :parseFilterDataMappings="parseFilterDataMappings"
              @submit="handleChangeActiveFilters"
            >
              <template #content="{ formValues, updateFormValues, fullObjects, updateFullObjects }">
                <InputTableFilter
                  :formValues="formValues"
                  :fullObjects="fullObjects"
                  @update:formValues="updateFormValues($event)"
                  @update:fullObjects="updateFullObjects($event)"
                />
              </template>
            </McTableFilter>
          </div>
          <b-row class="mt-4">
            <b-col v-for="(item, key) in costList" :key="key">
              <AmountCard :item="item" />
            </b-col>
          </b-row>
        </template>
        <template #viewManager="{ handleSetDataFields, dataFields, initialFields }">
          <TableViewManager
            :defaultColumnOrder="initialFields"
            :currentColumnOrder="dataFields"
            @setColumn="handleSetDataFields"
          />
        </template>
        <template #cell()="{ field, value }">
          {{ formatValues(field.key, value) }}
        </template>
        <!-- created on -->
        <template #cell(delivered_on)="{ value }">
          <div>{{ formatIndexDate(value).date }}</div>
          <div class="__time">{{ formatIndexDate(value).time }}</div>
        </template>
        <template #cell(cost)="{ value }">
          {{ value | formatNumber }}
        </template>
      </McTable>
    </div>
  </Layout>
</template>

<script>
import appConfig from '@/app.config'

import Layout from '@/router/layouts/main'
import PageHeader from '@/components/page-header-v2.vue'
import McButtonBack from '@/components/McButtonBack'
import McTable from '@/components/McTable'
import McListDownloader from '@/components/McListDownloader'
import TableViewManager from './components/TableViewManager'
import McTableFilter from '@/components/McTableFilter'
import parseFilterDataMappings from './lib/constants/parseFilterDataMappings'
import filterMappings from './lib/constants/filterMappings'
import InputTableFilter from './components/InputTableFilter'
import AmountCard from './components/AmountCard'
import { Helper } from '@/mixin/helper'
import { formatIndexDate } from '@/helpers/tzDate'
import { formatNumber } from '@/helpers/format-number'
import moment from 'moment'

import * as Report from '@/services/rest-api/lib/report/report.js'

import fields from './lib/fields/fields'
import { formatValues } from './lib/helpers/formatValues'
import omitBy from 'lodash/omitBy'
import FavoriteReportButton from '@/router/views/report/components/FavoriteReportButton'
import reportList from '../../lib/reportList.js'
import { mapGetters } from 'vuex'

export default {
  page: {
    title: 'Analysis Report',
    meta: [{ name: 'description', content: appConfig.description }],
  },
  components: {
    Layout,
    PageHeader,
    McButtonBack,
    McTable,
    McListDownloader,
    TableViewManager,
    InputTableFilter,
    McTableFilter,
    AmountCard,
    FavoriteReportButton
  },
  mixins: [fields],
  data() {
    return {
      params: {},
      metadata: null,
      activeFilters: {},
      reportKey: 'cost-analysis',
      filters: {},
      costList: [
        {
          title: 'report.text.totalCost',
          total: 0,
          color: 'green',
        },
        {
          title: 'report.text.costAverage',
          total: 0,
          color: 'blue',
        },
      ],
    }
  },
  computed: {
    ...mapGetters({
      hasAccess: 'identity/hasAccess',
    }),
    domain() {
      return this.report.domain
    },
    title() {
      return this.$t('report.report.costAnalysis')
    },
    subTitle() {
      return this.$t('report.text.costAnalysis')
    },
    apiClient() {
      let getReport = Report.getCostAnalysis(null, this.handleSetParams, this.handleSetMetadata)
      let finalFilters = this.activeFilters

      if (Object.keys(this.activeFilters).length === 0) {
        const costAnalysisFilter = this.$store.state.report.filterReport

        const activeFilters = {}
        for (let [filterName, value] of Object.entries(costAnalysisFilter)) {
          const renamedFilter = filterName === 'periode' ? 'delivered_on' : filterName

          // assign filters to activeFilters variable
          const filterMap = this.parseFilterDataMappings[renamedFilter]
          if (Array.isArray(filterMap)) {
            filterMap.forEach((filter, index) => {
              activeFilters[filter] = value[index]
            })
            continue
          }
          activeFilters[renamedFilter] = value
        }
        finalFilters = activeFilters
      }

      // mapping the activeFilters to be sent as query params
      const queryParamFilters = []
      Object.keys(finalFilters).forEach((key) => {
        let filterValue = finalFilters[key]

        if (key === 'delivered_on:gte' && filterValue) {
          filterValue = new Date(filterValue)
          filterValue.setHours(0, 0, 0)
          filterValue = filterValue.toISOString()
        } else if (key === 'delivered_on:lte' && filterValue) {
          filterValue = new Date(filterValue)
          filterValue.setHours(23, 59, 59)
          filterValue = filterValue.toISOString()
        }

        const filter = filterMappings[key]?.(key, filterValue) ?? undefined
        queryParamFilters.push(filter)
      })

      getReport = Report.getCostAnalysis(
        queryParamFilters,
        this.handleSetParams,
        this.handleSetMetadata
      )
      return {
        get: getReport,
      }
    },
    computedFields() {
      const computedFields = []

      this.fields.forEach((field) => {
        if (!field.isShow) return

        computedFields.push(field)
      })
      return computedFields.map((item, i) => ({ ...item, order: i + 1 }))
    },
    downloadPageOptions() {
      return [
        { label: this.$t('global.currentPage'), amount: this.metadata?.count, key: 'current_page' },
        { label: this.$t('global.allPages'), amount: this.metadata?.total_count, key: 'all' },
      ]
    },
    parseFilterDataMappings() {
      return parseFilterDataMappings
    },
    report() {
      return reportList.find((item) => item.key == this.reportKey)
    },
  },
  watch: {
    params: {
      async handler() {
        const { data } = await Report.totalCostAnalysis(this.params)
        if (data) {
          this.costList[0].total = data.total_amount
          this.costList[1].total = data.average_amount
        }
      },
      deep: true,
    },
  },
  mounted() {
    const { reportFilter, reportFilterValue } = Helper.methods.getFilterReport(this.reportKey)
    this.$store.commit('report/updateFilter', reportFilter)
    this.$store.commit('report/updateFilterValue', reportFilterValue)
    this.filters = { ...this.$store.state.report.filterReport }

    const mcTableFilter = this.$refs.mcTableFilter

    const filters = this.$store.state.report.filterReport
    for (let [filterName, value] of Object.entries(filters)) {
      const renamedFilter = filterName === 'periode' ? 'delivered_on' : filterName
      if (value && this.parseFilterDataMappings[renamedFilter]) {
        mcTableFilter.updateFormValues({ key: renamedFilter, value })

        // assign filters to activeFilters variable
        this.parseFilterDataMappings[renamedFilter].forEach((filter, index) => {
          this.activeFilters[filter] = value[index]
        })
      }
    }
  },
  methods: {
    formatNumber,
    formatValues,
    formatIndexDate,
    async handleDownload(format, dataAmount) {
      // Remove undefined params
      const params = omitBy(
        {
          ...this.params,
          format,
        },
        (value) => value === undefined
      )

      if (dataAmount === 'all') {
        delete params['limit']
        delete params['page']
      }

      const data = await Report.downloadCostAnalysis(params)
      if (data.errors) {
        this.$mcToast.error(Helper.methods.withErrorHandler())
        return
      }

      // Convert excel binary response into downloadable format
      const url = window.URL.createObjectURL(new Blob([data]))
      const link = document.createElement('a')
      link.href = url
      const formattedDate = moment().format('YYYY-MM-DD HH.mm.ss')
      link.setAttribute('download', `Cost Analysis - ${formattedDate}.xlsx`)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    },
    handleSetParams(params) {
      this.params = params
    },
    handleSetMetadata(metadata) {
      this.metadata = metadata
    },
    handleChangeActiveFilters(filters) {
      /*
        filters is Array of Object
        example:
        [
          {
            key: 'delivered_on:lte',
            values: '2023-08-29T17:00:00Z'
          }
        ]
        */

      filters.forEach((filter) => {
        // to reset the filter values
        this.$delete(this.activeFilters, filter.key)

        // to prevent set the filter in activeFilters if values are null or undefined
        if (filter.values === null || filter.values === undefined) return
        // to prevent set the filter in activeFilters if values are Array with length equal to 0
        if (Array.isArray(filter.values) && filter.values.length === 0) return

        // set the key and values in activeFilters
        this.$set(this.activeFilters, filter.key, filter.values)
      })

      this.updateFilter()
    },
    updateFilter() {
      const filters = {}
      for (const [key, val] of Object.entries(this.parseFilterDataMappings)) {
        if (Array.isArray(val)) {
          const value = []
          val.forEach((filter) => {
            this.activeFilters[filter] && value.push(this.activeFilters[filter])
          })
          if (value.length) filters[key] = value
        }
        if (typeof val === 'string' && this.activeFilters[val]) {
          filters[key] = this.activeFilters[val]
        }
      }
      this.$store.commit('report/updateFilter', filters)
    },
  },
}
</script>
<style lang="scss">
.__time {
  color: $mep-color-text-200;
}
</style>
